import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Disc, Loader2, CheckCircle2, AlertCircle, Building2 } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from '../contexts/TranslationContext';

import { API_BASE } from '../lib/apiBase';
const API = API_BASE;

export default function PartnerRegisterPage() {
    const { t } = useTranslation();
    const { token } = useParams();
    const navigate = useNavigate();
    const { login } = useAuth();
    
    const [invitation, setInvitation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        fetchInvitation();
    }, [token]);

    const fetchInvitation = async () => {
        try {
            const response = await axios.get(`${API}/api/partner-invitations/${token}`);
            setInvitation(response.data);
        } catch (err) {
            const errorMessage = err.response?.data?.detail || t('auth.partner_register.invalid_invitation', 'Invalid or expired invitation');
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name.trim()) {
            toast.error(t('auth.partner_register.enter_name', 'Please enter your name'));
            return;
        }

        if (password.length < 6) {
            toast.error(t('auth.reset.password_min_length', 'Password must be at least 6 characters'));
            return;
        }

        if (password !== confirmPassword) {
            toast.error(t('auth.reset.passwords_mismatch', 'Passwords do not match'));
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await axios.post(`${API}/api/partner-invitations/${token}/register`, {
                token,
                name: name.trim(),
                password
            });

            const { token: authToken, user } = response.data;
            localStorage.setItem('token', authToken);

            toast.success(t('auth.partner_register.success_toast', 'Account created successfully! Welcome to Owntune.'));

            if (user.role === 'repro_partner') {
                navigate('/artwork-jobs');
            } else {
                navigate('/dashboard');
            }

            window.location.reload();

        } catch (err) {
            const errorMessage = err.response?.data?.detail || t('auth.partner_register.create_failed', 'Failed to create account');
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-violet-50 to-slate-100 flex items-center justify-center p-4">
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-violet-600 mx-auto mb-4" />
                    <p className="text-slate-600">{t('auth.partner_register.validating', 'Validating invitation...')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-violet-50 to-slate-100 flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertCircle className="h-8 w-8 text-red-600" />
                        </div>
                        <CardTitle className="text-red-600">{t('auth.partner_register.invitation_invalid', 'Invitation Invalid')}</CardTitle>
                        <CardDescription>{error}</CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                        <p className="text-sm text-slate-500 mb-4">
                            {t('auth.partner_register.contact_support', 'Please contact support if you believe this is an error.')}
                        </p>
                        <Button variant="outline" onClick={() => navigate('/login')}>
                            {t('auth.go_to_login', 'Go to Login')}
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-violet-50 to-slate-100 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center mb-4">
                        <img src="/owntune-logo-dark.png" alt="Owntune" className="h-8 w-auto" />
                    </div>
                    <p className="text-slate-500">{t('auth.partner_register.setup', 'Partner Account Setup')}</p>
                </div>

                <Card className="mb-6 border-violet-200 bg-violet-50/50">
                    <CardContent className="pt-6">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <Building2 className="h-6 w-6 text-violet-600" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-slate-900">{invitation.partner_name}</h3>
                                <p className="text-sm text-slate-600">{t('auth.partner_register.partner_type', '{type} Partner', { type: invitation.partner_type })}</p>
                                <p className="text-xs text-violet-600 mt-1">
                                    <CheckCircle2 className="h-3 w-3 inline mr-1" />
                                    {t('auth.partner_register.invitation_verified', 'Invitation verified')}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('auth.partner_register.create_title', 'Create Your Account')}</CardTitle>
                        <CardDescription>
                            {t('auth.partner_register.create_desc', 'Set up your account to access Owntune as a partner')}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">{t('auth.email_field', 'Email')}</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={invitation.email}
                                    disabled
                                    className="bg-background"
                                    data-testid="register-email"
                                />
                                <p className="text-xs text-slate-500">
                                    {t('auth.partner_register.email_locked', 'Email is locked to the invitation')}
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="name">{t('auth.your_name', 'Your Name')}</Label>
                                <Input
                                    id="name"
                                    type="text"
                                    placeholder={t('auth.partner_register.name_placeholder', 'Enter your full name')}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    data-testid="register-name"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password">{t('common.password', 'Password')}</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder={t('auth.partner_register.password_placeholder', 'Create a password')}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    data-testid="register-password"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">{t('auth.reset.confirm_password_label', 'Confirm Password')}</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    placeholder={t('auth.partner_register.confirm_placeholder', 'Confirm your password')}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    data-testid="register-confirm-password"
                                />
                            </div>

                            <Button
                                type="submit"
                                className="w-full bg-violet-600 hover:bg-violet-700"
                                disabled={isSubmitting}
                                data-testid="register-submit"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        {t('auth.creating_account', 'Creating Account...')}
                                    </>
                                ) : (
                                    t('auth.create_account_button', 'Create Account')
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 text-center">
                            <p className="text-sm text-slate-500">
                                {t('auth.already_have_account', 'Already have an account?')}{' '}
                                <Button
                                    variant="link"
                                    className="text-violet-600 p-0 h-auto"
                                    onClick={() => navigate('/login')}
                                >
                                    {t('auth.signin_link', 'Sign in')}
                                </Button>
                            </p>
                        </div>
                    </CardContent>
                </Card>

                <p className="text-center text-xs text-slate-400 mt-6">
                    {t('auth.partner_register.terms_notice', "By creating an account, you agree to Owntune's terms of service.")}
                </p>
            </div>
        </div>
    );
}
